import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  provideAppInitializer,
  inject,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withNavigationErrorHandler } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { Amplify } from 'aws-amplify';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { handleRouterNavigationError } from '../utils/chunk-reload.utils';
import { httpAccessErrorInterceptor } from '../interceptors/http-access-error.interceptor';
import { httpAuthInterceptor } from '../interceptors/http-auth.interceptor';
import { httpErrorInterceptor } from '../interceptors/http-error.interceptor';
import { httpRetryInterceptor } from '../interceptors/http-retry.interceptor';
import { httpTimeoutInterceptor } from '../interceptors/http-timeout.interceptor';
import { policyDeniedInterceptor } from '../interceptors/policy-denied.interceptor';
import { EnvConfigService } from '../services/env-config-service/env-config-service';
import { ENV_CONFIG } from '../services/env-config-service/env-config.token';
import { BuildVersionCheckService } from '../services/build-version-check/build-version-check';
import { LocaleService } from '../services/locale/locale.service';
import { provideAppTransloco } from '../services/locale/transloco.config';
import { getAwsConfig } from '../aws-exports';

/**
 * Hard ceiling on each `provideAppInitializer` promise.
 *
 * Angular's `bootstrapApplication` awaits every initializer to
 * completion before producing the first view. If an initializer
 * resolves with an error (throw / reject), `bootstrapApplication`
 * rejects and `main.ts`'s `.catch(renderBootstrapError)` renders
 * the bootstrap-failure UI. But if an initializer **hangs** —
 * the underlying promise never settles — bootstrap waits forever,
 * `main.ts`'s catch never fires, and the user is stuck on the
 * pre-boot loader.
 *
 * The two initializers below await HTTP fetches that flow through
 * `httpTimeoutInterceptor` (15s default), so a stuck network call
 * should reject within that window. But Transloco's load chain
 * has its own retry logic that can outlast the per-request
 * timeout, and 3rd-party initializers added in the future have
 * no such guarantee. This wrapper turns ANY hang past the ceiling
 * into a deterministic reject, routing the failure through the
 * bootstrap-catch UI rather than leaving the user stuck.
 *
 * 20s is above the 15s interceptor ceiling (so a clean HTTP
 * timeout has time to bubble up as a normal rejection with a
 * useful error) and well below the 25s `BootHealthService`
 * stalled deadline (which can't help us here anyway — Angular
 * hasn't booted, so the in-Angular UI doesn't exist yet).
 */
const INITIALIZER_TIMEOUT_MS = 20_000;

function withInitializerTimeout<T>(label: string, fn: () => Promise<T>): () => Promise<T> {
  return () => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    return Promise.race([
      fn(),
      new Promise<T>((_, reject) => {
        timer = setTimeout(
          () =>
            reject(new Error(`Initializer '${label}' timed out after ${INITIALIZER_TIMEOUT_MS}ms`)),
          INITIALIZER_TIMEOUT_MS
        );
      }),
    ]).finally(() => {
      if (timer !== undefined) clearTimeout(timer);
    });
  };
}

const initializeApp = async () => {
  const envConfig = inject(EnvConfigService);
  const config = await envConfig.loadConfig();
  if (envConfig.isConfigValid()) {
    const awsConfig = getAwsConfig();
    Amplify.configure(awsConfig);
    console.debug('✅ Amplify configured');
  }
  return config;
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Angular Material overlay-based components (MatMenu, MatSelect,
    // MatDialog, MatSnackBar, MatTooltip) depend on Angular Animations
    // for the enter/leave transitions that gate the open state. Without
    // an animations provider some of these — notably MatMenu — never
    // transition to "open", so the click is registered but no menu ever
    // appears. `provideAnimationsAsync()` is the modern lazy loader that
    // pulls the animation engine in as a dynamic import on first use,
    // keeping the initial bundle lean.
    provideAnimationsAsync(),
    provideRouter(routes, withNavigationErrorHandler(handleRouterNavigationError)),
    ...provideAppTransloco(),
    // Interceptor order matters: each interceptor wraps the next, so the
    // first entry is the outermost wrapper.
    //   1. httpAccessErrorInterceptor — outermost so it can inspect the
    //      raw `HttpErrorResponse.error` body before httpErrorInterceptor
    //      normalises it into an `ApiError` (which drops the `hint` and
    //      `feature_id` the dispatcher needs). Fires a toast + CTA on
    //      matching feature-access denials, then re-throws unchanged.
    //   2. httpErrorInterceptor — normalises any error escaping the chain
    //      to ApiError | NetworkError. Outermost of the transport chain
    //      so retries see raw HttpErrorResponse statuses, not
    //      already-normalised domain errors.
    //   3. httpRetryInterceptor — retries idempotent (GET) requests once on
    //      transient failure. Wraps timeout + auth so each retry attempt is
    //      a fresh authed call with its own timeout window.
    //   4. httpAuthInterceptor  — attaches the bearer token (re-evaluated
    //      per attempt, so retries pick up refreshed tokens).
    //   5. httpTimeoutInterceptor — applies the per-request timeout to one
    //      attempt at a time.
    provideHttpClient(
      withInterceptors([
        httpAccessErrorInterceptor,
        policyDeniedInterceptor,
        httpErrorInterceptor,
        httpRetryInterceptor,
        httpAuthInterceptor,
        httpTimeoutInterceptor,
      ])
    ),
    EnvConfigService,
    {
      provide: ENV_CONFIG,
      useFactory: (envService: EnvConfigService) => {
        const config = envService.config();
        if (!config) {
          throw new Error('Environment configuration not loaded');
        }
        return config;
      },
      deps: [EnvConfigService],
    },
    {
      // Sonner-style placement for every `MatSnackBar.open(...)` call —
      // pairs with the `.snackbar-{success,error,info}` chrome defined
      // in styles/material-theme.css. Per-call options still win.
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
        duration: 4000,
      },
    },
    // Each awaited initializer is wrapped in `withInitializerTimeout`
    // so a hung promise can't stall `bootstrapApplication` forever
    // (which would leave `main.ts.catch` unfired and the user stuck
    // on the pre-boot loader with no escape). On timeout the
    // initializer rejects, bootstrap rejects, and the bootstrap-
    // failure UI renders.
    provideAppInitializer(withInitializerTimeout('env-config+amplify', initializeApp)),
    // Background deploy-detection: instantiates the service at boot and
    // kicks off its poll loop. Doesn't block app start (synchronous
    // initializer, no awaited promise) — runs in parallel with the
    // env-config bootstrap above. No timeout wrapper needed: the
    // initializer is synchronous (returns void, not a Promise).
    provideAppInitializer(() => {
      inject(BuildVersionCheckService).start();
    }),
    // Eagerly instantiate LocaleService at boot so its constructor effect
    // (which mirrors locale -> Transloco active lang + <html lang>) is
    // wired before the first navigation paints. Then await the active
    // dictionary fetch so the first paint already renders translated
    // strings — avoids the flash-of-untranslated-content where pipes
    // momentarily show the raw key (e.g. "sidebar.documents") before the
    // JSON arrives.
    provideAppInitializer(
      withInitializerTimeout('transloco-dict', async () => {
        const locale = inject(LocaleService);
        const transloco = inject(TranslocoService);
        await firstValueFrom(transloco.load(locale.locale()));
      })
    ),
  ],
};
